/**
 * CameraStream Component
 * Phone camera streaming for RBT sessions
 * Handles camera preview, streaming, and recording
 */

import React, { useRef, useState, useEffect } from 'react';
import { webRTCService } from '../services/WebRTCService';
import { Video, VideoOff, Camera, RotateCcw, Wifi, WifiOff, Circle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface CameraStreamProps {
  sessionNoteId: string;
  rbtId: string;
  rbtName: string;
  supervisorId: string;
  onStreamStart?: (streamSessionId: string) => void;
  onStreamEnd?: () => void;
  onError?: (error: string) => void;
  // Privacy Mode: when true, the outgoing video/audio is muted (black frames /
  // silence) without dropping the connection.
  paused?: boolean;
}

export const CameraStream: React.FC<CameraStreamProps> = ({
  sessionNoteId,
  rbtId,
  rbtName,
  supervisorId,
  onStreamStart,
  onStreamEnd,
  onError,
  paused = false,
}) => {
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [streamSessionId, setStreamSessionId] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [streamDuration, setStreamDuration] = useState(0);

  // Cleanup on unmount. Use endStream (not just cleanup) so the streamSessions
  // doc is marked 'ended' — otherwise leaving the page mid-stream (navigation,
  // tab close, session end) leaves a stale 'waiting' doc that inflates the
  // supervisor's Live Now count.
  useEffect(() => {
    return () => {
      webRTCService.endStream().catch(() => {
        webRTCService.cleanup();
      });
    };
  }, []);

  // Privacy Mode: mute/unmute the outgoing stream.
  useEffect(() => {
    webRTCService.setMediaEnabled(!paused);
  }, [paused]);

  // Stream duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (streaming) {
      interval = setInterval(() => {
        setStreamDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [streaming]);

  // Monitor connection state
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (streaming) {
      interval = setInterval(() => {
        const state = webRTCService.getConnectionState();
        setConnectionState(state || 'unknown');

        if (state === 'disconnected' || state === 'failed') {
          handleStreamError('Connection lost');
        }
      }, 1000);
    }
    return () => clearInterval(interval);
    // handleStreamError is stable for this effect's purpose; excluding it avoids re-subscribing every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartCamera = async () => {
    if (!videoRef.current) return;

    setError(null);
    try {
      await webRTCService.startLocalStream(videoRef.current, facingMode);
      setCameraReady(true);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to start camera';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  const handleSwitchCamera = async () => {
    if (!videoRef.current || !cameraReady) return;

    try {
      const newFacingMode = await webRTCService.switchCamera(videoRef.current, facingMode);
      setFacingMode(newFacingMode);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to switch camera';
      setError(errorMsg);
    }
  };

  const handleStartStream = async () => {
    if (!cameraReady) {
      setError('Please start the camera first');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      const sessionId = await webRTCService.createStreamSession(
        sessionNoteId,
        rbtId,
        rbtName,
        supervisorId,
        user?.companyId || ''
      );

      setStreamSessionId(sessionId);
      setStreaming(true);
      setStreamDuration(0);
      onStreamStart?.(sessionId);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to start streaming';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setConnecting(false);
    }
  };

  const handleStopStream = async () => {
    try {
      await webRTCService.endStream();
      setStreaming(false);
      setStreamSessionId(null);
      setConnectionState('');
      onStreamEnd?.();
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to stop stream';
      setError(errorMsg);
    }
  };

  const handleStreamError = (message: string) => {
    setError(message);
    setStreaming(false);
    onError?.(message);
  };

  const getConnectionStatusColor = (): string => {
    switch (connectionState) {
      case 'connected':
        return '#16a34a'; // green
      case 'connecting':
        return '#eab308'; // yellow
      case 'disconnected':
      case 'failed':
        return '#dc2626'; // red
      default:
        return '#6b6b6b'; // gray
    }
  };

  return (
    <div style={{
      backgroundColor: '#000',
      borderRadius: '12px',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Video Preview */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        backgroundColor: '#1f2937',
      }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          }}
        />

        {/* Overlay when camera not ready */}
        {!cameraReady && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
          }}>
            <VideoOff size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p style={{ opacity: 0.7, marginBottom: '16px' }}>Camera not active</p>
            <button
              onClick={handleStartCamera}
              style={{
                backgroundColor: '#4F6367',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              <Camera size={20} />
              Start Camera
            </button>
          </div>
        )}

        {/* Live indicator */}
        {streaming && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '6px 12px',
            borderRadius: '20px',
          }}>
            <Circle
              size={12}
              fill="#dc2626"
              color="#dc2626"
              style={{ animation: 'pulse 1s infinite' }}
            />
            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>LIVE</span>
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>
              {formatDuration(streamDuration)}
            </span>
          </div>
        )}

        {/* Connection status */}
        {streaming && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '6px 12px',
            borderRadius: '20px',
          }}>
            {connectionState === 'connected' ? (
              <Wifi size={16} color={getConnectionStatusColor()} />
            ) : (
              <WifiOff size={16} color={getConnectionStatusColor()} />
            )}
            <span style={{ color: getConnectionStatusColor(), fontSize: '12px', textTransform: 'capitalize' }}>
              {connectionState || 'waiting'}
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{
        padding: '12px',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        backgroundColor: '#2d2d2d',
      }}>
        {/* Switch Camera Button */}
        {cameraReady && (
          <button
            onClick={handleSwitchCamera}
            disabled={!cameraReady}
            style={{
              backgroundColor: '#374151',
              color: 'white',
              padding: '12px',
              borderRadius: '50%',
              border: 'none',
              cursor: cameraReady ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
            }}
            title="Switch Camera"
          >
            <RotateCcw size={20} />
          </button>
        )}

        {/* Stream Toggle Button */}
        {cameraReady && (
          <button
            onClick={streaming ? handleStopStream : handleStartStream}
            disabled={connecting}
            style={{
              backgroundColor: streaming ? '#dc2626' : '#16a34a',
              color: 'white',
              padding: '12px 32px',
              borderRadius: '24px',
              border: 'none',
              cursor: connecting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              opacity: connecting ? 0.7 : 1,
            }}
          >
            {streaming ? (
              <>
                <VideoOff size={20} />
                Stop Stream
              </>
            ) : (
              <>
                <Video size={20} />
                {connecting ? 'Connecting...' : 'Start Stream'}
              </>
            )}
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          borderTop: '1px solid #fecaca',
          padding: '12px',
          color: '#dc2626',
          fontSize: '14px',
          textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      {/* Stream Info */}
      {streamSessionId && (
        <div style={{
          backgroundColor: '#1f2937',
          padding: '8px 12px',
          color: '#9ca3af',
          fontSize: '12px',
          textAlign: 'center',
        }}>
          Stream ID: {streamSessionId.slice(0, 8)}...
        </div>
      )}

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default CameraStream;
